import { Role } from 'auth';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { withPrivateAccess } from '@/modules/auth/hocs/withPrivateAccess.tsx';
import { useGetCurrentBusinessId } from '@/modules/common/hooks/useGetCurrentBusinessId.ts';
import { useClientsListQuery } from '@/modules/common/queries/clients.ts';
import '@/modules/common/styles/common.css';
import ClientItem from '@/modules/admin-dashboard/components/items/ClientItem.tsx';
import CreateClientModal from '@/modules/admin-dashboard/components/modals/CreateClientModal.tsx';
import { Button, EmptyListState, ModalProviderContext, PageLayout, SearchField, Spinner } from 'ui';

const Clients = () => {
  const { openModal } = useContext(ModalProviderContext);
  const businessId = useGetCurrentBusinessId();

  const itemRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const loadMoreRef = useRef<HTMLDivElement>(null);

  const [searchValue, setSearchValue] = useState('');

  useEffect(() => {
    setSearchValue('');
  }, [businessId]);

  const { hasNextPage, fetchNextPage, data, isLoading, isFetchingNextPage } = useClientsListQuery({
    page: 1,
    page_size: 18,
    filter_by_name: searchValue,
  });

  const clients = useMemo(() => {
    return (data?.pages || []).flatMap(({ data }) => data);
  }, [data?.pages]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const currentLoadMoreRef = loadMoreRef.current;
    if (!currentLoadMoreRef || !hasNextPage || isFetchingNextPage) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1, rootMargin: '100px' },
    );

    observer.observe(currentLoadMoreRef);

    return () => {
      if (currentLoadMoreRef) {
        observer.unobserve(currentLoadMoreRef);
      }
    };
  }, [hasNextPage, fetchNextPage, isFetchingNextPage]);

  const headerAction = (
    <div className="flex max-w-[650px] flex-wrap-reverse w-full mt-5 md:mt-0 justify-end items-center gap-3">
      <SearchField
        placeholder="Search"
        value={searchValue}
        onChange={setSearchValue}
        className="w-[240px]"
      />
      <div className="w-fit">
        <Button onClick={() => openModal(CreateClientModal)} variant="primary">
          Add new client
        </Button>
      </div>
    </div>
  );

  return (
    <PageLayout title="Clients" headerAction={headerAction}>
      <EmptyListState
        title="No clients yet"
        description={
          'To create a client, click Add new client. Clients are a way to group tasks, events, reports, and documents by related entity, and see audit log in a single place.'
        }
        isLoading={isLoading}
        isSearchUsed={!!searchValue}
        itemsCount={clients.length}
      />
      {isLoading && (
        <div className="flex h-full w-full items-center justify-center">
          <Spinner size="lg" variant="primary" />
        </div>
      )}
      {!!clients.length && (
        <div className="w-full flex flex-col space-y-2">
          {clients.map(client => (
            <div key={client.id} ref={el => (itemRefs.current[client.id] = el)}>
              <ClientItem client={client} />
            </div>
          ))}
          {hasNextPage && (
            <div ref={loadMoreRef} className="flex justify-center items-center h-16 w-full">
              {isFetchingNextPage ? (
                <Spinner size="md" variant="primary" />
              ) : (
                <div className="text-sm text-gray-500">Scroll for more</div>
              )}
            </div>
          )}
        </div>
      )}
    </PageLayout>
  );
};
export default withPrivateAccess(Clients, Role.COMPLIANCE_ADMIN);
